import { NotificationsPopover } from '@components/Header/NotificationsPopover/NotificationsPopover';
import { PopoverBadge } from '@components/Header/NotificationsPopover/PopoverBadge';
import RegionPicker from '@components/Header/RegionPicker';
import ReportsDrawer from '@components/Header/ReportsDrawer/ReportsDrawer';
import { SeasonPopoverWrapper } from '@components/Header/SeasonPopover/SeasonPopoverWrapper';
import { MachineryFileUploader } from '@components/MachineryFileUploader/MachineryFileUploader';
import { SubscriptionSetup } from '@components/SubscriptionSetup/SubscriptionSetup';
import { useNotificationsCenter } from '@contexts/NotificationsCenterContext';
import { SETTINGS_MENU_OPEN_EVENT } from '@core/constants/settingsMenu';
import { SupportedEntitlements } from '@core/models/entitlements';
import { setToken } from '@cw-elements/config';
import { TopbarCustomSlot } from '@cw-elements/host-kit';
import { useOrganization } from '@hooks/queries/useOrganization';
import { useEntitlements } from '@hooks/useEntitlements';
import { useImageryFlags } from '@hooks/useImageryFlags';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { RootState } from '@redux/reducers';
import { routePaths } from '@screens/routes';
import { useBoundStore } from '@state/store';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router';
import { PropertySelector } from './PropertySelector/PropertySelector';
import { TopbarNavigationMFE } from './TopbarNavigationMFE/TopbarNavigationMFE';

// Interaction-only drawers: lazy, mounted on first open. ReportsDrawer stays eager (feeds the report badge).
const DroneDrawer = lazy(() => import('@components/Header/DroneDrawer'));
const HelpDrawer = lazy(() => import('@components/Header/HelpDrawer'));
const NdviReportDrawer = lazy(() => import('@components/Header/NdviReportDrawer/NdviReportDrawer'));
const BulkXlsxDrawer = lazy(() =>
  import('@components/Header/ReportsDrawer/BulkXlsxForm/BulkXlsxDrawer').then((m) => ({
    default: m.BulkXlsxDrawer,
  }))
);

// Latch: keep the drawer mounted once opened so its close animation plays.
// Guarded set-state-during-render (fires once, no loop); a ref trips react-hooks/refs.
function useOpenedOnce(isOpen: boolean) {
  const [opened, setOpened] = useState(false);
  if (isOpen && !opened) {
    setOpened(true);
  }
  return opened;
}

export function TopbarNavigation() {
  const { unreadCount } = useNotificationsCenter();
  const entitlements = useEntitlements();
  const regions = useBoundStore((state) => Object.values(state.regions.entities));
  const { baseUser } = useSelector((state: RootState) => state.User);
  const [showClassicTopbarNavigation] = useLocalStorage(
    'cropwise_experience_switched_to_classic_topbar_navigation',
    false,
    'localstorage'
  );
  const { organization } = useOrganization();

  const [reportsDrawerVisible, setReportsDrawerVisible] = useState<boolean>(false);
  const [propertyPopoverVisible, setPropertyPopoverVisible] = useState<boolean>(false);
  const [droneDrawerVisible, setDroneDrawerVisible] = useState<boolean>(false);
  const [helpDrawerVisible, setHelpDrawerVisible] = useState<boolean>(false);
  const [reportNotifications, setReportNotifications] = useState<number>(0);
  const [ndviReportDrawerVisible, setNdviReportDrawerVisible] = useState<boolean>(false);
  const [bulkXlsxDrawerVisible, setBulkXlsxDrawerVisible] = useState<boolean>(false);
  const [bulkXlsxDrawerType, setBulkXlsxDrawerType] = useState<'timeseries' | 'histogram'>(
    'timeseries'
  );

  // Latch each interaction-only drawer so its chunk loads on first open.
  const droneDrawerOpened = useOpenedOnce(droneDrawerVisible);
  const helpDrawerOpened = useOpenedOnce(helpDrawerVisible);
  const ndviReportDrawerOpened = useOpenedOnce(ndviReportDrawerVisible);
  const bulkXlsxDrawerOpened = useOpenedOnce(bulkXlsxDrawerVisible);

  const notificationDomNode = document.querySelector<HTMLElement>('#topbar-notification-icon')
    ?.parentElement;
  const reportDomNode = document.querySelector<HTMLElement>('#topbar-report-icon')?.parentElement;

  const showProperty = useMatch({ path: routePaths.view, end: false });
  const hasWeeklyReportsEntitlement = entitlements.has(
    SupportedEntitlements.WEEKLY_FIELD_INSIGHTS_REPORT
  );
  const {
    FlagShowMyCropwiseTopbar,
    FlagShowNdviReportDrawer,
    FlagNewReportsDrawer,
  } = useImageryFlags(organization);

  const hasAccessToReportsDrawer = FlagNewReportsDrawer || hasWeeklyReportsEntitlement;

  const hasAccessToWeeklyFieldInsightsReport =
    FlagShowNdviReportDrawer || hasWeeklyReportsEntitlement;
  // Config CropwiseElements
  useEffect(() => {
    const tokens = JSON.parse(localStorage.getItem('tokens') ?? 'null');
    if (tokens) {
      const token = tokens['access_token'] as string;
      setToken(token);
    }
  }, [baseUser]);

  const droneDrawerOff = useCallback(() => {
    setDroneDrawerVisible(false);
  }, []);

  const ndviReportDrawerOff = useCallback(() => {
    setNdviReportDrawerVisible(false);
    setReportsDrawerVisible(hasAccessToReportsDrawer);
  }, [hasAccessToReportsDrawer]);

  const bulkXlsxDrawerOff = useCallback(() => {
    setBulkXlsxDrawerVisible(false);
    setReportsDrawerVisible(hasAccessToReportsDrawer);
  }, [hasAccessToReportsDrawer]);

  const handleOpenBulkXlsxReport = useCallback((type: 'timeseries' | 'histogram') => {
    setBulkXlsxDrawerType(type);
    setBulkXlsxDrawerVisible(true);
  }, []);

  const reportsDrawerOff = useCallback(() => {
    setReportsDrawerVisible(false);
  }, []);

  const closeHelpDrawer = useCallback(() => {
    setHelpDrawerVisible(false);
    globalThis.dispatchEvent(new CustomEvent(SETTINGS_MENU_OPEN_EVENT));
  }, []);

  return (
    <>
      <TopbarNavigationMFE
        reportsDrawerVisible={reportsDrawerVisible}
        setReportsDrawerVisible={setReportsDrawerVisible}
        setDroneDrawerVisible={setDroneDrawerVisible}
        setHelpDrawerVisible={setHelpDrawerVisible}
        setReportNotifications={setReportNotifications}
      />
      <TopbarCustomSlot>
        {showProperty && (
          <div className="flex gap-6">
            {(!FlagShowMyCropwiseTopbar || showClassicTopbarNavigation) && (
              <PropertySelector
                propertyPopoverVisible={propertyPopoverVisible}
                setPropertyPopoverVisible={setPropertyPopoverVisible}
              />
            )}
            {regions.length > 1 && <RegionPicker />}
            <SeasonPopoverWrapper />
          </div>
        )}
      </TopbarCustomSlot>
      {organization && hasAccessToReportsDrawer && (
        <ReportsDrawer
          visible={reportsDrawerVisible}
          onClose={reportsDrawerOff}
          setNDVIReportDrawerVisible={setNdviReportDrawerVisible}
          setReportNotifications={setReportNotifications}
          onOpenBulkXlsxReport={handleOpenBulkXlsxReport}
        />
      )}
      {bulkXlsxDrawerOpened && (
        <Suspense fallback={null}>
          <BulkXlsxDrawer
            visible={bulkXlsxDrawerVisible}
            type={bulkXlsxDrawerType}
            close={bulkXlsxDrawerOff}
            onBack={bulkXlsxDrawerOff}
          />
        </Suspense>
      )}
      {helpDrawerOpened && (
        <Suspense fallback={null}>
          <HelpDrawer visible={helpDrawerVisible} close={closeHelpDrawer} />
        </Suspense>
      )}
      {droneDrawerOpened && (
        <Suspense fallback={null}>
          <DroneDrawer visible={droneDrawerVisible} close={droneDrawerOff} />
        </Suspense>
      )}
      <NotificationsPopover />
      <MachineryFileUploader />
      <SubscriptionSetup />
      {hasAccessToWeeklyFieldInsightsReport && ndviReportDrawerOpened ? (
        <Suspense fallback={null}>
          <NdviReportDrawer visible={ndviReportDrawerVisible} close={ndviReportDrawerOff} />
        </Suspense>
      ) : null}
      {notificationDomNode
        ? createPortal(
            <PopoverBadge
              className="absolute top-0 right-0"
              size="small"
              count={unreadCount}
              offset={[2, -4]}
            ></PopoverBadge>,
            notificationDomNode
          )
        : null}
      {reportDomNode
        ? createPortal(
            <PopoverBadge
              className="absolute top-0 right-0"
              size="small"
              count={reportNotifications}
              offset={[2, -4]}
            ></PopoverBadge>,
            reportDomNode
          )
        : null}
    </>
  );
}
